import type {IInitConfig} from 'flagsmith/types';
import type {Role} from 'next-auth';

// Define types for Datadog configuration
type DatadogConfig = {
  enabled: boolean;
  environment?: string;
  release?: string;
  clientToken?: string;
  applicationId?: string;
  site: string;
  service: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  testTenants: Array<string>;
  enableDebugLogging: boolean;
};

export const config = {
  datadog: {
    enabled: false,
    environment: 'dev',
    release: process.env.NEXT_PUBLIC_RELEASE_NAME,
    clientToken: process.env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN,
    applicationId: process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID,
    site: `datadoghq.com`,
    service: `console`,
    sessionReplaySampleRate: 0,
    sessionSampleRate: 0,
    testTenants: [],
    enableDebugLogging: false,
  } as DatadogConfig,
  flags: {
    client: {
      environmentID: 'dev',
    } as IInitConfig,
    server: {
      environmentID: process.env.NEXT_PUBLIC_FLAGSMITH_ENV_ID,
    } as IInitConfig,
    common: {
      enableLogs: false,
    } as IInitConfig,
  },
  sst: {
    name: `console`,
    region: `us-east-1`,
  },
  nextauth: {
    /** List of rules that are allowed to sign in to our console */
    allowedRoles: [`regularUser`],
    sessionMaxAge: 1000 * 60 * 60 * 24 * 30, // A month
    invalidateWhenIdpSessionExpired: {} as Record<string, number>, // tenant ID → session expiration in hours
    pages: {
      signIn: `/login`,
      signOut: `/logout`,
      defaultRedirect: {
        admin: `/dashboard`,
        reviewer: `/user-access-reviews/my-campaigns`,
        appOwner: `/discover/applications`,
        accessUser: `/my-access`,
        unauthorized: `/unauthorized`,
        campaignOwner: `/user-access-reviews/campaigns`,
      },
    },
  },
  deployEnv: process.env.NEXT_PUBLIC_ENVIRONMENT as `local` | `preview` | `dev` | `stg` | `prod`,
  api: {
    shouldMock: false,
    prefix: `/api/proxy`,
    timeout: 1000 * 5,
    table: {
      pageSize: 20,
    },
  },
  cdn: {
    base: process.env.NEXT_PUBLIC_CDN_BASE_URL,
    /**
     * The base URL for the CDN where the system icons are stored.
     */
    manifest: `/integrations-manifest/`,
  },
  contact: {
    support: {
      email: `support@linxsecurity.io`,
    },
  },
  logs: {
    enabled: false,
    level: process.env.LOG_LEVEL || `info`,
    msgPrefix: `[console]`,
    browser: {
      enabled: false,
      level: process.env.NEXT_PUBLIC_LOG_LEVEL || `info`,
    },
  },
  pages: {
    discover: {
      applications: {
        flags: {},
      },
      accounts: {
        flags: {} as const,
      },
      resources: {
        flags: {},
      },
      nonHumans: {
        flags: {},
      },
      orphanAccounts: {
        href: `/insights/orphan-accounts`,
        roles: [`admin`] satisfies Array<Role>,
        flags: {
          isPageVisible: `ui_orphan-account-page_visible`,
        },
      },
    },
    insights: {
      flags: {
        isEnabled: `ui_insights-page_visible`,
      },
      breakGlassAccounts: {
        href: `/insights/break-glass-accounts`,
        roles: [`admin`] satisfies Array<Role>,
        flags: {
          isEnabled: `ui_break-glass-accounts-page_visible`,
          ownerColumnVisibility: `ui_break-glass-accounts-owner-columns_visible`,
        },
      },
      scheduleReports: {
        href: `/insights/scheduled-reports`,
        roles: [`admin`] satisfies Array<Role>,
        flags: {
          isEnabled: `ui_saved_query_reports`,
        },
      },
    },
    integrations: {
      flags: {} as const,
    },
    issues: {
      href: `/issues`,
      roles: [`admin`, `app_owner`] satisfies Array<Role>,
      flags: {
        isResolutionEnabled: `ui_issues-remediation_visible`,
        isZeroValuesVisible: `ui_issues_widget_zeros_visible`,
        isNotificationPlatformEnabled: `ui_custom_issues_notifcations_enabled`,
        isWorkflowsEnabled: `ui_custom_issues_workflows_enabled`,
      } as const,
    },
  },
};
